import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Form,
    Container,
    Row,
    Col,
    InputGroup,
    Button,
    Spinner,
    Badge
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { getAuthToken } from "../../utils/auth";
import PaginationBar from "../common/PaginationBar";
import { ITEMS_PER_PAGE } from "../../constants/appConstants";

const LogsManagement = () => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();

    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState("");


    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const itemsPerPage = ITEMS_PER_PAGE;

    const fetchLogs = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/user/userActions`);

            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", itemsPerPage);
            if (search.trim()) url.searchParams.append("query", search.trim());

            const res = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            const result = await res.json();

            if (result.status) {
                setLogs(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
            } else {
                setLogs([]);
            }
        } catch (err) {
            console.error("Error fetching logs", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, itemsPerPage, token, currentPage, search]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search]);

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h3>User Action Logs</h3>
                </Col>

                <Col xs="6">
                    <InputGroup>
                        <Form.Control
                            placeholder="Search user, action, details..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />


                        <Button onClick={fetchLogs}>Search</Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>User</th>
                                <th>Action</th>
                                <th>Details</th>
                                <th>Endpoint</th>
                                <th>Method</th>
                                <th>IP</th>
                                <th>Status</th>
                                <th>Error</th>
                                <th>Time</th>
                            </tr>
                        </thead>

                        <tbody>
                            {logs.length > 0 ? (
                                logs.map((log) => (
                                    <tr key={log.id}>
                                        <td>{log.id}</td>
                                        <td className="text-nowrap">
                                            <div>{log.phone || "-"}</div>
                                            {log.userId && (
                                                <Link to={`/admin/users/${log.userId}`} className="small">
                                                    User #{log.userId}
                                                </Link>
                                            )}
                                        </td>
                                        <td><Badge bg="secondary">{log.action}</Badge></td>
                                        <td className="small" style={{ minWidth: 240, whiteSpace: "pre-wrap" }}>
                                            {log.details || ""}
                                        </td>
                                        <td>{log.endpoint}</td>
                                        <td>{log.method}</td>
                                        <td>{log.ip}</td>
                                        <td>
                                            {log.success ? (
                                                <Badge bg="success">OK</Badge>
                                            ) : (
                                                <Badge bg="danger">FAILED</Badge>
                                            )}
                                        </td>
                                        <td className="text-danger">{log.errorMessage}</td>
                                        <td>{new Date(log.createdAt).toLocaleString()}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="10" className="text-center text-muted">
                                        No logs found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default LogsManagement;
